/**
 * OVERRIDE ListingBody.jsx
 * REASON: o core sempre renderiza a paginação nativa (`Pagination` do
 * semantic-ui-react, `Menu`) sem permitir troca por variação. A variação
 * `newsCenter` (docs/specs/blocks/news-listing-page.md §3.2) precisa da sua
 * própria paginação (HTML simples + tokens, sem semantic-ui), fiel ao mockup
 * "1 2 3 4 5 … 11 ›". Único ponto de mudança em relação ao original: se a
 * variação declarar `paginationComponent`, ele é usado no lugar do
 * `Pagination` nativo — variações que não declaram continuam como antes.
 *
 * Resto do arquivo é cópia fiel de
 * core/packages/volto/src/components/manage/Blocks/Listing/ListingBody.jsx
 * (verificar ao atualizar o core), com UMA simplificação: o `Headline`
 * original usa `@plone/volto-slate` (`renderLinkElement`) + `github-slugger`
 * para transformar `data.headline` num heading com âncora deep-link. Nenhuma
 * das nossas variações (`newsCarousel`, `newsCenter`) usa `data.headline`
 * (cada uma desenha seu próprio cabeçalho — ver NewsCenterTemplate), e
 * `odc.volto` não depende de `@plone/volto-slate`/`github-slugger`
 * (resolução pnpm estrita — conferido: não resolvem fora do `core/packages/
 * volto`). Trocado por um heading simples, sem o link de âncora — perda
 * aceitável de um recurso não usado neste projeto, em vez de puxar uma
 * dependência só para código morto.
 */
import React, { createRef } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import cx from 'classnames';
import { Pagination, Dimmer, Loader } from 'semantic-ui-react';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import config from '@plone/volto/registry';
import withQuerystringResults from '@plone/volto/components/manage/Blocks/Listing/withQuerystringResults';

import paginationLeftSVG from '@plone/volto/icons/left-key.svg';
import paginationRightSVG from '@plone/volto/icons/right-key.svg';

import SlotRenderer from '@plone/volto/components/theme/SlotRenderer/SlotRenderer';

const Headline = ({
  headlineTag: HeadlineTag,
  id,
  data = {},
  listingItems,
}) => (
  <HeadlineTag
    id={id}
    className={cx('headline', {
      emptyListing: !listingItems?.length > 0,
    })}
  >
    {data.headline}
  </HeadlineTag>
);

const ListingBody = withQuerystringResults((props) => {
  const {
    data = {},
    isEditMode,
    listingItems,
    totalPages,
    onPaginationChange,
    variation,
    currentPage,
    prevBatch,
    nextBatch,
    isFolderContentsListing,
    hasLoaded,
    id,
    content,
  } = props;

  let ListingBodyTemplate;
  // Legacy support if template is present
  const variations = config.blocks?.blocksConfig['listing']?.variations || [];
  const defaultVariation = variations.filter((item) => item.isDefault)?.[0];

  if (data.template && !data.variation) {
    const legacyTemplateConfig = variations.find(
      (item) => item.id === data.template,
    );
    ListingBodyTemplate = legacyTemplateConfig.template;
  } else {
    ListingBodyTemplate =
      variation?.template ?? defaultVariation?.template ?? null;
  }

  const listingRef = createRef();

  const NoResults = variation?.noResultsComponent
    ? variation.noResultsComponent
    : config.blocks?.blocksConfig['listing'].noResultsComponent;

  const HeadlineTag = data.headlineTag || 'h2';

  // Variação pode declarar seu próprio componente de paginação
  // (docs/specs/blocks/news-listing-page.md §3.2) — único desvio do core.
  const PaginationComponent = variation?.paginationComponent;

  return (
    <>
      {data.headline && (
        <Headline
          headlineTag={HeadlineTag}
          id={id}
          listingItems={listingItems}
          data={data}
          isEditMode={isEditMode}
        />
      )}
      <SlotRenderer name="aboveListingItems" content={content} data={data} />
      {listingItems?.length > 0 ? (
        <div ref={listingRef}>
          <ListingBodyTemplate
            items={listingItems}
            isEditMode={isEditMode}
            {...data}
            {...variation}
          />
          {totalPages > 1 &&
            (PaginationComponent ? (
              <PaginationComponent
                activePage={currentPage}
                totalPages={totalPages}
                prevBatch={prevBatch}
                nextBatch={nextBatch}
                onPageChange={(activePage) => {
                  !isEditMode &&
                    listingRef.current.scrollIntoView({ behavior: 'smooth' });
                  onPaginationChange(null, { activePage });
                }}
              />
            ) : (
              <div className="pagination-wrapper">
                <Pagination
                  activePage={currentPage}
                  totalPages={totalPages}
                  onPageChange={(e, { activePage }) => {
                    !isEditMode &&
                      listingRef.current.scrollIntoView({
                        behavior: 'smooth',
                      });
                    onPaginationChange(e, { activePage });
                  }}
                  firstItem={null}
                  lastItem={null}
                  prevItem={{
                    content: <Icon name={paginationLeftSVG} size="18px" />,
                    icon: true,
                    'aria-disabled': !prevBatch,
                    className: !prevBatch ? 'disabled' : null,
                  }}
                  nextItem={{
                    content: <Icon name={paginationRightSVG} size="18px" />,
                    icon: true,
                    'aria-disabled': !nextBatch,
                    className: !nextBatch ? 'disabled' : null,
                  }}
                />
              </div>
            ))}
        </div>
      ) : isEditMode ? (
        <div className="listing message" ref={listingRef}>
          {isFolderContentsListing && (
            <FormattedMessage
              id="No items found in this container."
              defaultMessage="No items found in this container."
            />
          )}
          {hasLoaded && NoResults && (
            <NoResults isEditMode={isEditMode} {...data} />
          )}
          <Dimmer active={!hasLoaded} inverted>
            <Loader indeterminate size="small">
              <FormattedMessage id="loading" defaultMessage="Loading" />
            </Loader>
          </Dimmer>
        </div>
      ) : (
        <div className="emptyListing">
          {hasLoaded && NoResults && (
            <NoResults isEditMode={isEditMode} {...data} />
          )}
          <Dimmer active={!hasLoaded} inverted>
            <Loader indeterminate size="small">
              <FormattedMessage id="loading" defaultMessage="Loading" />
            </Loader>
          </Dimmer>
        </div>
      )}
    </>
  );
});

export default injectIntl(ListingBody);
